import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { PlayerProvider } from '@/context/PlayerContext';
import { AppLayout } from '@/components/shell/AppLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';

import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import AccountSelect from '@/pages/AccountSelect';
import Onboarding from '@/pages/Onboarding';
import Dashboard from '@/pages/Dashboard';
import CalendarPage from '@/pages/CalendarPage';
import TasksPage from '@/pages/TasksPage';
import FinancePage from '@/pages/FinancePage';
import NotesPage from '@/pages/NotesPage';
import NotificationsPage from '@/pages/NotificationsPage';
import AssistantsPage from '@/pages/AssistantsPage';
import RoutinesPage from '@/pages/RoutinesPage';
import NetworkPage from '@/pages/NetworkPage';
import PlannerSpacePage from '@/pages/PlannerSpacePage';
import BooksPage from '@/pages/BooksPage';
import EmailsPage from '@/pages/EmailsPage';
import SettingsPage from '@/pages/SettingsPage';
import AdminPanel from '@/pages/AdminPanel';
import { FullScreenLoader } from '@/components/Loader';

const Loader = () => <FullScreenLoader />;

const Protected = ({ children }) => {
  const { user, loading, activeAccount } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'super_master') return <Navigate to="/plannerapp/admin" replace />;
  if (user.role === 'assistant' && !activeAccount) return <Navigate to="/select-account" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
};

const AdminOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'super_master') return <Navigate to="/plannerapp" replace />;
  return children;
};

// Mapea la preferencia "Vista de entrada" del perfil a la pantalla de inicio.
const ENTRY_ROUTES = {
  calendar_hero: '/plannerapp/calendar',
  financial_hub: '/plannerapp/finance',
  project_board: '/plannerapp/tasks',
  daily_digest: null, // Panel (dashboard)
};

// Solo redirige una vez por sesión (login / apertura de la PWA), de modo que
// navegar manualmente al Panel siga funcionando con normalidad.
const EntryGate = () => {
  const { user } = useAuth();
  const target = ENTRY_ROUTES[user?.entry_view];
  const already = typeof window !== 'undefined' && sessionStorage.getItem('pp_entry_applied');
  if (target && !already) {
    sessionStorage.setItem('pp_entry_applied', '1');
    return <Navigate to={target} replace />;
  }
  return <Dashboard />;
};

function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <AuthProvider>
          <PlayerProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/select-account" element={<AccountSelect />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/plannerapp/admin" element={<AdminOnly><AdminPanel /></AdminOnly>} />
                <Route path="/plannerapp" element={<Protected><AppLayout /></Protected>}>
                  <Route index element={<EntryGate />} />
                  <Route path="calendar" element={<CalendarPage />} />
                  <Route path="tasks" element={<TasksPage />} />
                  <Route path="finance" element={<FinancePage />} />
                  <Route path="notes" element={<NotesPage />} />
                  <Route path="routines" element={<RoutinesPage />} />
                  <Route path="plannerspace" element={<PlannerSpacePage />} />
                  <Route path="books" element={<BooksPage />} />
                  <Route path="emails" element={<EmailsPage />} />
                  <Route path="network" element={<NetworkPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="assistants" element={<AssistantsPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster position="top-right" theme="dark" richColors />
            </BrowserRouter>
          </PlayerProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
